import React from 'react'
import AntdIcon from '@ant-design/icons'
import type { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon'
import type { IconComponentProps } from '@ant-design/icons/lib/components/Icon'
import type { LucideIcon } from 'lucide-react'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { createAntdCompatIcon, type IconMode } from './shadcn/createIcon'
import ShadcnIcon from './shadcn/Icon'

/**
 * The design toggle, applied to the icon backend.
 *
 * `src/icons/antd-compat` is the ONE import path every icon call site uses,
 * and until now it was a pure re-export of `@ant-design/icons`. This file is
 * the switch its header always promised: with the toggle off each icon renders
 * antd's own glyph component with the props and ref it was given, so the DOM
 * is byte-identical to importing from the package (asserted across a matrix in
 * `__tests__/icon-seam-parity.test.tsx`); with it on, the same call site
 * renders the lucide glyph the map picked for it.
 *
 * Read `src/design-mode/index.ts` first, then `src/antd/Button.tsx` - the
 * worked example this follows. The difference worth knowing here is that the
 * icon seam is not one dispatcher but ~336, generated from `icon-map.txt` into
 * `./glyphs` — ONE MODULE PER GLYPH, because rollup assigns a module to exactly
 * one chunk and a single shared module would hoist the union of the app's icons
 * into the entry chunk. An antd icon the map does not name is not dispatched at
 * all: it passes straight through and stays antd in both modes.
 * Brand marks are the bulk of that (lucide ships none); `icon-map.txt` lists
 * the rest.
 *
 * COST, stated rather than buried: each dispatcher INSTANCE holds its own
 * `useDesignMode()` subscription, exactly as Button/Space/Typography/Tag do,
 * and icons are the densest element in this app - a table page renders
 * hundreds. That is the same accepted mechanism, at the largest reach it has
 * been applied to yet, and it is the strongest argument yet for collapsing the
 * hook to a single context read before the next port. See the design-toggle
 * entry in AGENTS.md.
 */

/** The shape of one of antd's named glyph components (`PlusOutlined`). */
type AntdGlyphComponent = React.ForwardRefExoticComponent<
  AntdIconProps & React.RefAttributes<HTMLSpanElement>
>

/**
 * Builds one icon's dispatcher: antd's glyph while the toggle is off, the
 * lucide-backed compat glyph while it is on.
 *
 * The shadcn component is built once, at module scope, rather than per render -
 * a component identity that changed every render would remount the icon (and
 * anything inside a custom `component`) on every parent update.
 */
export const createIconDispatcher = (
  name: string,
  AntdGlyph: AntdGlyphComponent,
  Glyph: LucideIcon,
  mode: IconMode = 'outline'
) => {
  const ShadcnGlyph = createAntdCompatIcon(name, Glyph, mode)

  const Dispatcher = React.forwardRef<HTMLSpanElement, AntdIconProps>(
    (props, ref) => {
      const designMode = useDesignMode()

      if (designMode === 'shadcn') {
        return <ShadcnGlyph {...props} ref={ref} />
      }

      return <AntdGlyph {...props} ref={ref} />
    }
  )

  // antd names every glyph component this way, and the icon picker and a few
  // devtools reads lean on it.
  Dispatcher.displayName = `AntdIcon(${name})`

  return Dispatcher
}

export type IconDispatcher = ReturnType<typeof createIconDispatcher>

/**
 * The seam's default export: antd's custom-SVG `<Icon component={Svg} />`
 * wrapper, dispatched the same way.
 *
 * This is the one every WasteHero-owned glyph goes through
 * (`src/icons/*`, `src/storybook/wh-icons-antd`), so both branches render the
 * project's own artwork - only antd's frame around it changes.
 */
const IconDispatcher = React.forwardRef<HTMLSpanElement, IconComponentProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') {
      return <ShadcnIcon {...props} ref={ref} />
    }

    return <AntdIcon {...props} ref={ref} />
  }
)

IconDispatcher.displayName = 'AntdIcon'

export default IconDispatcher
